import React from 'react';
import { Info, ExternalLink, Search, Shield, TrendingUp, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о перенаправлениях URL и их влиянии
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        О перенаправлениях URL и их значении
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-blue-500" />
          Что такое перенаправления URL?
        </h3>
        <p>
          <strong>Перенаправление URL (редирект)</strong> — это механизм, который автоматически переводит пользователя или поискового робота с одного URL-адреса на другой. Редиректы широко используются при реорганизации сайтов, переходе на HTTPS, объединении дублирующихся страниц и многих других сценариях.
        </p>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-500" />
          Типы HTTP-редиректов и их значение
        </h3>
        <ul>
          <li>
            <strong>301 (Permanent Redirect):</strong> Указывает, что ресурс был перемещен навсегда. Это предпочтительный тип редиректа для SEO, поскольку он передает большую часть «веса» ссылочной массы от исходного URL к новому. Поисковые системы обновляют свои индексы на новый URL.
          </li>
          <li>
            <strong>302 (Found / Temporary Redirect):</strong> Указывает, что ресурс временно доступен по другому URL. Поисковые системы сохраняют исходный URL в индексе. Не рекомендуется для постоянных изменений структуры сайта.
          </li>
          <li>
            <strong>303 (See Other):</strong> Используется для перенаправления после отправки формы, чтобы предотвратить повторную отправку при обновлении страницы.
          </li>
          <li>
            <strong>307 (Temporary Redirect):</strong> Строгая версия 302 редиректа, сохраняет метод запроса (GET, POST и т.д.) при перенаправлении.
          </li>
          <li>
            <strong>308 (Permanent Redirect):</strong> Строгая версия 301 редиректа, гарантирует сохранение метода запроса при перенаправлении.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Search className="w-5 h-5 text-green-500" />
          Влияние редиректов на SEO
        </h3>
        <p>
          Правильная реализация редиректов критически важна для здоровья SEO-показателей сайта. Неправильное использование может привести к потере трафика, снижению позиций в выдаче и ухудшению пользовательского опыта.
        </p>
        <ul>
          <li><strong>301 vs 302:</strong> Для постоянных изменений структуры сайта всегда используйте 301 редиректы, так как они передают до 90-99% «ссылочного веса». Редиректы 302 передают значительно меньшую часть.</li>
          <li><strong>Цепочки редиректов:</strong> Старайтесь избегать цепочек из нескольких перенаправлений (A → B → C). Идеально иметь прямой редирект A → C, поскольку каждое дополнительное перенаправление замедляет загрузку и может привести к потере части SEO-сигналов.</li>
          <li><strong>Редиректы на релевантный контент:</strong> Перенаправляйте на страницы с похожим контентом. Редирект с продуктовой страницы на главную может рассматриваться как софт-404 (мягкая ошибка).</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Рекомендации Яндекса по редиректам</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Поисковая система Яндекс рекомендует использовать постоянные (301) редиректы для переезда сайта на новый домен или изменения URL-структуры. Важно настроить редиректы между всеми страницами старого и нового сайта. При переезде на HTTPS также следует использовать 301 редирект со всех HTTP-версий страниц.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Влияние редиректов на скорость загрузки сайта
        </h3>
        <p>
          Каждое перенаправление требует дополнительного времени для обработки, поскольку браузер должен отправить новый запрос на сервер. Это замедляет загрузку страницы и может негативно влиять на пользовательский опыт.
        </p>
        <ul>
          <li><strong>Дополнительные HTTP-запросы:</strong> Каждый редирект инициирует новый HTTP-запрос, что увеличивает время загрузки минимум на один RTT (Round Trip Time).</li>
          <li><strong>Цепочки редиректов:</strong> Цепочки из нескольких перенаправлений значительно усугубляют проблему, так как каждое звено добавляет задержку.</li>
          <li><strong>Мобильные устройства:</strong> На мобильных устройствах с менее стабильным подключением негативный эффект от редиректов усиливается.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-500" />
          Безопасность и редиректы
        </h3>
        <p>
          Перенаправления могут представлять угрозу безопасности, если не реализованы должным образом. Распространенные проблемы включают:
        </p>
        <ul>
          <li><strong>Open Redirect Vulnerability:</strong> Когда сайт перенаправляет на URL-адрес, указанный в параметре, без надлежащей проверки. Это может использоваться для фишинговых атак.</li>
          <li><strong>HTTP → HTTPS редиректы:</strong> Обязательны для защиты конфиденциальности пользователей, особенно на страницах с формами и личными данными.</li>
          <li><strong>Небезопасные редиректы:</strong> Могут использоваться злоумышленниками для перенаправления пользователей на вредоносные сайты.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Распространенные ошибки при настройке редиректов
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Использование временных (302) редиректов вместо постоянных (301) для постоянных изменений</li>
            <li>Создание длинных цепочек редиректов или циклических перенаправлений</li>
            <li>Редирект на нерелевантные страницы вместо тематически близких</li>
            <li>Отсутствие редиректов для старых URL при изменении структуры сайта</li>
            <li>Непроверенные редиректы, позволяющие перенаправлять на любые внешние ресурсы (open redirect)</li>
          </ul>
        </div>

        <h3>Как правильно реализовать редиректы в России</h3>
        <p>
          В российском сегменте интернета есть несколько особенностей, которые стоит учитывать при настройке редиректов:
        </p>
        <ol>
          <li><strong>Кириллические домены:</strong> При редиректе с кириллического домена на латинский или наоборот, убедитесь, что используется корректное punycode-преобразование.</li>
          <li><strong>Региональные поддомены:</strong> Для многорегиональных сайтов часто используются поддомены вида msk.site.ru, spb.site.ru. При редиректе с них важно сохранять региональную релевантность.</li>
          <li><strong>Учет Яндекса:</strong> Яндекс имеет значительную долю на российском рынке поиска, поэтому следует учитывать его рекомендации наравне с Google.</li>
          <li><strong>Мобильная версия:</strong> В России высокая доля мобильного трафика, убедитесь, что редиректы корректно работают для мобильных пользователей.</li>
          <li><strong>HTTPS-миграция:</strong> Внедрение HTTPS становится стандартом и в России, поэтому настройте корректные 301 редиректы с HTTP на HTTPS.</li>
        </ol>

        <h3>Инструменты для проверки и мониторинга редиректов</h3>
        <ul>
          <li><strong>Инструменты Яндекса:</strong> Яндекс.Вебмастер позволяет отслеживать и анализировать редиректы на вашем сайте.</li>
          <li><strong>Screaming Frog:</strong> Позволяет анализировать цепочки редиректов и находить проблемы в структуре перенаправлений.</li>
          <li><strong>Google Search Console:</strong> Помогает выявлять проблемы с редиректами, которые могут влиять на индексацию сайта.</li>
          <li><strong>Мониторинг серверных логов:</strong> Регулярно проверяйте логи сервера на наличие ошибок с перенаправлениями и проблемными URL.</li>
          <li><strong>Автоматические уведомления:</strong> Настройте оповещения о появлении новых 404 ошибок, которые могут свидетельствовать о проблемах с редиректами.</li>
        </ul>

        <p className="font-semibold mt-6">
          Правильная настройка и регулярный мониторинг редиректов — важнейшая часть технического SEO, обеспечивающая сохранение трафика при изменениях на сайте и хороший пользовательский опыт.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;